import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { 
  inventoryService, 
  CreateProductDto, 
  UpdateProductDto, 
  AddStockDto, 
  AdjustStockDto,
  ProductQueryParams
} from '../services/inventory.service';

export const useProducts = (search?: string) => {
  return useQuery({
    queryKey: ['products', search],
    queryFn: () => inventoryService.getProducts(search),
  });
};

export const useProductsPage = (params: ProductQueryParams) => {
  return useQuery({
    queryKey: ['products', 'page', params],
    queryFn: () => inventoryService.getProductsPage(params),
    placeholderData: keepPreviousData,
  });
};

export const useInventorySummary = () => {
  return useQuery({
    queryKey: ['products', 'summary'],
    queryFn: () => inventoryService.getInventorySummary(),
  });
};

export const useProduct = (id: string) => {
  return useQuery({
    queryKey: ['product', id],
    queryFn: () => inventoryService.getProduct(id),
    enabled: !!id,
  });
};

export const useLowStockProducts = () => {
  return useQuery({
    queryKey: ['products', 'low-stock'],
    queryFn: () => inventoryService.getLowStockProducts(),
  });
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProductDto) => inventoryService.createProduct(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create product');
    },
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductDto }) =>
      inventoryService.updateProduct(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      toast.success('Product updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update product');
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => inventoryService.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete product');
    },
  });
};

export const useUpdateStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, stock }: { id: string; stock: number }) =>
      inventoryService.updateStock(id, stock),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      toast.success('Stock updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update stock');
    },
  });
};

export const useAddStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AddStockDto }) =>
      inventoryService.addStock(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements', variables.id] });
      toast.success('Stock added successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add stock');
    },
  });
};

export const useAdjustStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdjustStockDto }) =>
      inventoryService.adjustStock(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements', variables.id] });
      toast.success('Stock adjusted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to adjust stock');
    },
  });
};

export const useStockMovements = (productId: string, limit: number = 50) => {
  return useQuery({
    queryKey: ['stock-movements', productId, limit],
    queryFn: () => inventoryService.getStockMovements(productId, limit),
    enabled: !!productId,
  });
};

export const useAllStockMovements = (filters?: any, limit: number = 100) => {
  return useQuery({
    queryKey: ['stock-movements', 'all', filters, limit],
    queryFn: () => inventoryService.getAllStockMovements(filters, limit),
  });
};

// Composite hook that returns products for components that expect useInventory
export const useInventory = () => {
  const { data: products = [], isLoading, error } = useProducts();
  
  return {
    products,
    isLoading,
    error,
  };
};